// components/Interstitial/Interstitial.tsx
import { Fragment } from "preact";

export type InterstitialStep = {
  kind: "interstitial";
  id: string;
  title: string;
  subtitle: string;
  body?: string;
  layout?: "" | "split";
  imageSrc?: string;
  imageAlt?: string;
  imageClassName?: string;
};

type Props = { step: InterstitialStep };

// render title with safe line breaks (supports '\n' or literal '<br/>')
function TitleWithBreaks({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const parts = (text ?? "").split(/\r?\n|<br\s*\/?>/i);
  return (
    <h2 className={className}>
      {parts.map((line, i) => (
        <Fragment key={i}>
          {line}
          {i < parts.length - 1 && <br />}
        </Fragment>
      ))}
    </h2>
  );
}

export function Interstitial({ step }: Props) {
  const isSplit = step.layout === "split";

  if (!isSplit) {
    // Centered version (unchanged)
    return (
      <section className="cq-flex cq-flex-col cq-gap-4 md:cq-gap-8 cq-items-center cq-px-4 cq-text-balance">
        {step.imageSrc && (
          <img
            src={`${import.meta.env.BASE_URL}${step.imageSrc}`}
            alt={step.imageAlt ?? step.title}
            className="cq-mx-auto cq-w-28 cq-h-28 md:cq-w-56 md:cq-h-56"
          />
        )}

        <div className="cq-text-center cq-w-full">
          <TitleWithBreaks
            text={step.title}
            className="cq-text-4xl md:cq-text-6xl cq-leading-tight cq-text-white"
          />
          <h3 className="cq-text-[#00BBFF] cq-text-lg md:cq-text-2xl cq-text-center cq-font-medium cq-py-4 md:cq-py-8">
            {step.subtitle}
          </h3>
          {step.body && (
            <p className="cq-text-base md:cq-text-2xl cq-text-white cq-text-center cq-mx-auto cq-max-w-[550px]">
              {step.body}
            </p>
          )}
        </div>
      </section>
    );
  }

  // SPLIT version: full-viewport background image with text over it, black base
  return (
    <section className="cq-flex cq-flex-col cq-gap-20 cq-w-full cq-h-full cq-bg-black cq-justify-center">
      <TitleWithBreaks
        text={step.title}
        className="cq-text-white cq-font-semibold cq-text-2xl md:cq-text-4xl cq-text-center"
      />
      <div className="cq-flex cq-flex-row cq-gap-6 cq-justify-center">
        <span
          className="cq-w-6 cq-h-6 md:cq-w-8 md:cq-h-8 cq-rounded-full cq-bg-cfa-bright-blue-50 cq-animate-bounce"
          style="animation-delay: 0s;"
        ></span>
        <span
          className="cq-w-6 cq-h-6 md:cq-w-8 md:cq-h-8 cq-rounded-full cq-bg-cfa-bright-blue-50 cq-animate-bounce"
          style="animation-delay: 0.1s;"
        ></span>
        <span
          className="cq-w-6 cq-h-6 md:cq-w-8 md:cq-h-8 cq-rounded-full cq-bg-cfa-bright-blue-50 cq-animate-bounce"
          style="animation-delay: 0.2s;"
        ></span>
        <span
          className="cq-w-6 cq-h-6 md:cq-w-8 md:cq-h-8 cq-rounded-full cq-bg-cfa-bright-blue-50 cq-animate-bounce"
          style="animation-delay: 0.3s;"
        ></span>
        <span className="cq-sr-only">Generating your report...</span>
      </div>
    </section>
  );
}
